import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import api from '../../api';
import Loader from '../../components/Loader';
import Nutrition from '../../components/Nutrition';
import constants from '../../common/utils/constants';
import { getTranslation } from '../../utils/translate';
import notify from '../../components/Notification/helper';
import { useRefreshToken } from '../../common/hooks/useRefreshToken';
import { chooseInputComponent } from '../../common/utils/components';
import { buildTranslationPayload, buildRelationshipPayload } from '../../common/utils/payloads';

// Constants
const TABLE_HEADERS = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'Name',
  },
  {
    id: 'summary',
    numeric: false,
    disablePadding: false,
    label: 'Summary',
  },
];

const INITIAL_BEEF_CUT_PAYLOAD = {
  status: '',
  image: '',
  svg_icon: '',
  translations: {
    create: [],
    update: [],
    delete: [],
  },
};

function RetailersCreate() {
  useRefreshToken();

  // Route params
  const { beefCutId } = useParams();
  const crudOperation = beefCutId ? 'update' : 'create';

  // Redux selectors
  const recipesStore = useSelector((state) => state.recipes);
  const cookingMethodsStore = useSelector((state) => state.cookingMethods);
  const beefProductBrandsStore = useSelector((state) => state.beefProductBrands);
  const beefCutsStore = useSelector((state) => state.beefCuts);

  // State - Entity data
  const [beefCut, setBeefCut] = useState({});
  const [beefCutPayload, setBeefCutPayload] = useState(INITIAL_BEEF_CUT_PAYLOAD);

  // State - Relationships
  const [linkedRecipes, setLinkedRecipes] = useState([]);
  const [linkedCookingMethods, setLinkedCookingMethods] = useState([]);
  const [linkedBeefProductBrands, setLinkedBeefProductBrands] = useState([]);

  // State - Translations
  const [translatedBeefCut, setTranslatedBeefCut] = useState({});
  const [frenchTranslatedBeefCut, setFrenchTranslatedBeefCut] = useState({});

  // State - API
  const [apiCallStatus, setApiCallStatus] = useState(constants.idle);

  // Computed data - Formatted rows for relationship tables
  const beefCutRows = useMemo(
    () =>
      beefCutsStore.data.map((row) => {
        const translation = getTranslation(
          row,
          { key: 'languages_id', code: 'en-US' },
          'translations'
        );
        return {
          id: row.id,
          name: translation?.name,
          description: translation?.description,
        };
      }),
    [beefCutsStore.data]
  );

  const recipeRows = useMemo(
    () =>
      recipesStore.data.map((row) => {
        const translation = getTranslation(
          row,
          { key: 'languages_code', code: 'en-US' },
          'layout_translations'
        );
        return {
          id: row.id,
          name: translation?.name,
          description: translation?.summary?.substring(0, 99) + '...',
        };
      }),
    [recipesStore.data]
  );

  const cookingMethodRows = useMemo(
    () =>
      cookingMethodsStore.data.map((row) => {
        const translation = getTranslation(row);
        return {
          id: row.id,
          name: translation?.Method,
          description: translation?.Description,
        };
      }),
    [cookingMethodsStore.data]
  );

  const beefProductBrandRows = useMemo(
    () =>
      beefProductBrandsStore.data.map((row) => {
        const translation = getTranslation(row);
        return {
          id: row.id,
          name: translation?.name,
          description: '...',
        };
      }),
    [beefProductBrandsStore.data]
  );

  // Handlers
  const handleInputChange = useCallback(
    (value, field, languageCode, translationId) => {
      const keyValuePair = { [field]: value };

      if (languageCode) {
        keyValuePair.languages_code = languageCode;
        const translationObject = beefCutPayload.translations[crudOperation]?.find(
          (translation) => translation.id === translationId
        );
        if (translationObject) {
          translationObject[field] = value;
        }
      }

      setBeefCutPayload({
        ...beefCutPayload,
        ...keyValuePair,
      });
    },
    [beefCutPayload, crudOperation]
  );

  const handleUpdateBeefCut = useCallback(async () => {
    try {
      setApiCallStatus(constants.fetching);
      await api.updateBeefCut(beefCutPayload);
      notify('success', 'Beef cut updated successfully');
      setApiCallStatus(constants.fetched);
    } catch (error) {
      console.error('Failed to update beef cut:', error);
      notify('error', 'Failed to update beef cut');
      setApiCallStatus(constants.error);
    }
  }, [beefCutPayload]);

  const handleAddRelationship = useCallback((relationshipList, selectedItems) => {
    if (!selectedItems?.length) return;
    relationshipList.push({
      id: selectedItems[0],
      name: '',
    });
  }, []);

  // Build form sections with translations
  const formSections = useMemo(
    () => [
      {
        title: 'Overview',
        fields: [
          {
            label: 'Name',
            name: 'name',
            type: 'text',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              values: {
                'en-US': {
                  id: translatedBeefCut.id,
                  value: translatedBeefCut.name,
                },
                'fr-FR': {
                  id: frenchTranslatedBeefCut.id,
                  value: frenchTranslatedBeefCut.name,
                },
              },
            },
          },
          {
            label: 'Description',
            name: 'description',
            type: 'textarea',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              values: {
                'en-US': {
                  id: translatedBeefCut.id,
                  value: translatedBeefCut.description,
                },
                'fr-FR': {
                  id: frenchTranslatedBeefCut.id,
                  value: frenchTranslatedBeefCut.description,
                },
              },
            },
          },
          {
            label: 'Tips',
            name: 'tips',
            type: 'textarea',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              values: {
                'en-US': {
                  id: translatedBeefCut.id,
                  value: translatedBeefCut.tips,
                },
                'fr-FR': {
                  id: frenchTranslatedBeefCut.id,
                  value: frenchTranslatedBeefCut.tips,
                },
              },
            },
          },
        ],
      },
      {
        title: 'Images',
        fields: [
          {
            label: 'Image',
            name: 'image',
            type: 'image',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              value: beefCutPayload.image,
            },
          },
          {
            label: 'SVG Icon',
            name: 'svg_icon',
            type: 'image',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              value: beefCutPayload.svg_icon,
            },
          },
        ],
      },
      {
        title: 'Collections',
        fields: [
          {
            label: 'Parent',
            name: 'parent_group',
            type: 'm2o',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              title: 'Beef Cuts',
              rows: beefCutRows,
              headCells: TABLE_HEADERS,
              items: [
                {
                  id: beefCut?.parent_group?.id,
                  name: getTranslation(
                    beefCut.parent_group,
                    {
                      key: 'languages_id',
                      code: 'en-US',
                    },
                    'translations'
                  )?.name,
                },
              ],
            },
          },
          {
            label: 'Recipes',
            name: 'recipes',
            type: 'm2m',
            handleInputChange: (selectedItems) => {
              handleAddRelationship(linkedRecipes, selectedItems);
              handleInputChange(
                buildRelationshipPayload(
                  beefCutPayload.recipes,
                  'create',
                  selectedItems.map((selectedItemId) => ({
                    beef_product_id: beefCutId,
                    recipe_id: {
                      id: selectedItemId,
                    },
                  }))
                ),
                'recipes'
              );
            },
            config: {
              size: '12',
              translations: [],
              title: 'Recipes',
              rows: recipeRows,
              headCells: TABLE_HEADERS,
              items: linkedRecipes,
            },
          },
          {
            label: 'Cooking Methods',
            name: 'cooking_methods',
            type: 'm2m',
            handleInputChange: (selectedItems) => {
              handleInputChange(
                buildRelationshipPayload(
                  beefCutPayload.cooking_methods,
                  'create',
                  selectedItems.map((selectedItemId) => ({
                    beef_product: beefCutId,
                    cooking_method: {
                      id: selectedItemId,
                    },
                  }))
                ),
                'cooking_methods'
              );
            },
            config: {
              size: '12',
              translations: [],
              title: 'Cooking Methods',
              rows: cookingMethodRows,
              headCells: TABLE_HEADERS,
              items: linkedCookingMethods,
            },
          },
          {
            label: 'Beef Product Brands',
            name: 'beef_product_brands',
            type: 'm2m',
            handleInputChange: (selectedItems) => {
              handleInputChange(
                buildRelationshipPayload(
                  beefCutPayload.cooking_methods,
                  'create',
                  selectedItems.map((selectedItemId) => ({
                    beef_product: beefCutId,
                    beef_product_branding_id: {
                      id: selectedItemId,
                    },
                  }))
                ),
                'beef_product_brandings'
              );
            },
            config: {
              size: '12',
              translations: [],
              title: 'Beef Product Brands',
              rows: beefProductBrandRows,
              headCells: TABLE_HEADERS,
              items: linkedBeefProductBrands,
            },
          },
        ],
      },
      {
        title: 'Visibility',
        fields: [
          {
            label: 'Status',
            name: 'status',
            type: 'status',
            handleInputChange,
            config: {
              size: '12',
              translations: [],
              value: beefCutPayload.status,
            },
          },
        ],
      },
    ],
    [
      handleInputChange,
      handleAddRelationship,
      translatedBeefCut,
      frenchTranslatedBeefCut,
      beefCutPayload,
      beefCut,
      beefCutRows,
      recipeRows,
      cookingMethodRows,
      beefProductBrandRows,
      linkedRecipes,
      linkedCookingMethods,
      linkedBeefProductBrands,
      beefCutId,
    ]
  );

  // Effects
  useEffect(() => {
    if (!beefCutId) return;

    const initializeBeefCut = async () => {
      try {
        setApiCallStatus(constants.fetching);

        const fetchedBeefCut = await api.getBeefProduct(beefCutId);
        setBeefCut(fetchedBeefCut);

        // Build payload and translations
        const requestPayload = buildTranslationPayload(
          fetchedBeefCut.translations,
          'languages_id'
        );

        const recipes = fetchedBeefCut.recipes?.map((recipeJunction) => ({
          id: recipeJunction.recipe_id.id,
          name: getTranslation(
            recipeJunction.recipe_id,
            { key: 'languages_code', code: 'en-US' },
            'layout_translations'
          )?.name,
        })) || [];

        const cookingMethods = fetchedBeefCut.cooking_methods?.map(
          (cookingMethodJunction) => ({
            id: cookingMethodJunction.cooking_method.id,
            name: getTranslation(cookingMethodJunction.cooking_method)?.Method,
          })
        ) || [];

        const beefProductBrands = fetchedBeefCut.beef_product_brandings?.map(
          (brandJunction) => ({
            id: brandJunction.beef_product_branding_id.id,
            name: getTranslation(brandJunction.beef_product_branding_id)?.name,
          })
        ) || [];

        // Update payload
        setBeefCutPayload({
          id: beefCutId,
          status: fetchedBeefCut.status,
          image: fetchedBeefCut.image,
          svg_icon: fetchedBeefCut.svg_icon,
          ...requestPayload,
        });

        // Update translations
        setTranslatedBeefCut(
          getTranslation(
            requestPayload.translations,
            { key: 'languages_id', code: 'en-US' },
            'update'
          ) || {}
        );

        setFrenchTranslatedBeefCut(
          getTranslation(
            requestPayload.translations,
            { key: 'languages_id', code: 'fr-FR' },
            'update'
          ) || {}
        );

        // Update relationships
        setLinkedRecipes(recipes);
        setLinkedCookingMethods(cookingMethods);
        setLinkedBeefProductBrands(beefProductBrands);

        setApiCallStatus(constants.fetched);
      } catch (error) {
        console.error('Failed to initialize beef cut:', error);
        notify('error', 'Failed to load beef cut details');
        setApiCallStatus(constants.error);
      }
    };

    initializeBeefCut();
  }, [beefCutId]);


  if (apiCallStatus === constants.fetching) {
    return <Loader />;
  }

  const buttonLabel = beefCutId ? 'Update Beef Cut' : 'Create Beef Cut';

  return (
    <Box sx={{ flexGrow: 1, my: 3 }}>
      <Typography className="form-title" variant="h2">
        Create New Retailer
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7} lg={6}>
          {formSections.map((section, sectionIndex) => (
            <Box key={section.title || `section-${sectionIndex}`}>
              {section.title && (
                <Typography
                  className="section-title"
                  variant="h2"
                  sx={{
                    mt: 5,
                    mb: 2,
                  }}
                >
                  {section.title}
                </Typography>
              )}

              <Grid container spacing={2}>
                {section.fields.map((field) => (
                  <Grid
                    item
                    key={field.name}
                    xs={['color', 'font', 'dropdown'].includes(field?.type) ? 4 : 12}
                  >
                    {chooseInputComponent(field)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          <Button
            className="form-button"
            sx={{
              my: 5,
              backgroundColor: 'var(--brand-color)',
            }}
            size="large"
            variant="contained"
            onClick={handleUpdateBeefCut}
          >
            {buttonLabel}
          </Button>
        </Grid>

        {beefCut.Nutrients && (
          <Grid item xs={12} md={5} lg={6}>
            <Typography
              className="section-title"
              variant="h2"
              sx={{
                mt: 5,
                mb: 2,
              }}
            >
              Nutrition
            </Typography>
            <Nutrition nutrition={beefCut.Nutrients} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default RetailersCreate;